import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../api";
import Seo from "../components/Seo";
import { ArrowLeft, Clock, BookOpen, ArrowRight, ChevronUp } from "lucide-react";

export default function ArticleDetail() {
    const { id } = useParams();
    const [a, setA] = useState(null);
    const [related, setRelated] = useState([]);
    const [progress, setProgress] = useState(0);
    const [toc, setToc] = useState([]);
    const [activeHeading, setActiveHeading] = useState("");
    const bodyRef = useRef(null);

    useEffect(() => {
        api.get(`/articles/${id}`)
            .then((r) => setA(r.data))
            .catch(() => setA(false));
        api.get("/articles?limit=30").then((r) => setRelated((r.data || []).filter((x) => x.id !== id).slice(0, 3)));
    }, [id]);

    // Build TOC from rendered h2/h3 once body mounts
    useEffect(() => {
        if (!a || !bodyRef.current) return;
        const nodes = bodyRef.current.querySelectorAll("h2, h3");
        const entries = [];
        nodes.forEach((node, i) => {
            const slug = node.textContent.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || `s-${i}`;
            node.id = slug;
            entries.push({ slug, text: node.textContent, level: node.tagName === "H3" ? 2 : 1 });
        });
        setToc(entries);
    }, [a]);

    // Scroll progress + active heading
    useEffect(() => {
        const onScroll = () => {
            const h = document.documentElement;
            const scrolled = h.scrollTop;
            const max = h.scrollHeight - h.clientHeight;
            setProgress(max > 0 ? Math.min(100, Math.round((scrolled / max) * 100)) : 0);
            // active heading
            if (bodyRef.current) {
                const nodes = bodyRef.current.querySelectorAll("h2, h3");
                let current = "";
                nodes.forEach((n) => {
                    const rect = n.getBoundingClientRect();
                    if (rect.top < 200) current = n.id;
                });
                if (current) setActiveHeading(current);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, [toc]);

    if (a === null) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[var(--body)]">Loading…</div>;
    if (a === false) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[var(--body)]">Article not found.</div>;

    const readTime = a.read_time || Math.max(3, Math.ceil((a.body || "").split(" ").length / 220));

    return (
        <div data-testid="article-detail">
            {a && <Seo title={a.title} description={(a.excerpt || a.title || "").slice(0, 155)} image={a.image_url} />}
            {/* Scroll progress bar */}
            <div className="fixed top-[64px] md:top-[78px] left-0 right-0 z-30 h-[2px] bg-transparent">
                <div className="h-full bg-[var(--taupe)] transition-all" style={{ width: `${progress}%` }} />
            </div>

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
                <Link to="/learn" className="inline-flex items-center gap-1.5 text-[13px] text-[var(--body)] hover:text-[var(--ink)] mb-8 fast-link">
                    <ArrowLeft size={14} /> Back to Learn
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* TOC sidebar (desktop) */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-28">
                            <div className="overline mb-3">In this article</div>
                            {toc.length === 0 ? (
                                <div className="text-[12px] text-[var(--body)]">No sections.</div>
                            ) : (
                                <ul className="space-y-2 border-l border-[var(--line)] pl-4">
                                    {toc.map((t) => (
                                        <li key={t.slug}>
                                            <a
                                                href={`#${t.slug}`}
                                                className={`block text-[13px] transition fast-link ${
                                                    activeHeading === t.slug ? "text-[var(--ink)] font-medium" : "text-[var(--body)] hover:text-[var(--ink)]"
                                                } ${t.level === 2 ? "pl-3" : ""}`}
                                                data-testid={`toc-${t.slug}`}
                                            >
                                                {t.text}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </aside>

                    {/* Main reading column */}
                    <article className="lg:col-span-7">
                        <div className="overline mb-3 flex items-center gap-3 flex-wrap">
                            <span>{a.category || a.tag || "Wellness"}</span>
                            <span className="text-[var(--line)]">·</span>
                            <span className="inline-flex items-center gap-1"><Clock size={11} /> {readTime} min read</span>
                        </div>
                        <h1 className="h-display text-[34px] sm:text-[48px] lg:text-[56px] leading-[1.05] mb-6 text-[var(--ink)]">{a.title}</h1>
                        {a.cover_image && (
                            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 bg-[var(--bg-alt)]">
                                <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                            </div>
                        )}
                        {a.excerpt && (
                            <p className="text-[19px] sm:text-[20px] text-[var(--ink-soft)] italic-serif mb-8 leading-relaxed border-l-2 border-[var(--taupe)] pl-5">{a.excerpt}</p>
                        )}
                        <div
                            ref={bodyRef}
                            className="article-body prose-ucare text-[16px] sm:text-[17px] text-[var(--ink-soft)] leading-[1.85]"
                            /* admin-curated HTML, single-tenant */
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.body || "") }}
                        />

                        <div className="mt-14 pt-8 border-t border-[var(--line-soft)] flex flex-wrap items-center justify-between gap-4">
                            <Link to="/shop" className="btn btn-primary" data-testid="article-shop-cta">
                                Explore our Formulations <ArrowRight size={14} />
                            </Link>
                            <button
                                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                className="text-[13px] text-[var(--body)] hover:text-[var(--ink)] inline-flex items-center gap-1 fast-link"
                                data-testid="back-to-top"
                            >
                                Back to top <ChevronUp size={14} />
                            </button>
                        </div>
                    </article>

                    {/* Related articles */}
                    <aside className="lg:col-span-2">
                        <div className="sticky top-28">
                            <div className="overline mb-3 flex items-center gap-1"><BookOpen size={11} /> Related</div>
                            <div className="space-y-3">
                                {related.length === 0 ? (
                                    <div className="text-[12px] text-[var(--body)]">No related articles yet.</div>
                                ) : related.map((r) => (
                                    <Link
                                        key={r.id}
                                        to={`/learn/${r.id}`}
                                        className="block group"
                                        data-testid={`related-${r.id}`}
                                    >
                                        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-[var(--bg-alt)] mb-2 group-hover:opacity-90 transition">
                                            {r.cover_image ? (
                                                <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full grid place-items-center text-3xl">📖</div>
                                            )}
                                        </div>
                                        <div className="text-[12.5px] font-medium text-[var(--ink)] leading-snug line-clamp-3 group-hover:text-[var(--taupe)] transition">{r.title}</div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
